import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { ShoppingBag, X, Minus, Plus, Crown, Loader2, CheckCircle2, Truck, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => (Number.isInteger(Number(n)) ? `$${Number(n)}` : `$${Number(n).toFixed(2)}`);
export const BUNDLE_SKUS = ["bundle_clean_shaven", "bundle_bearded", "bundle_smooth"];
const GOLD = ["#d4af37", "#c5a059", "#f5d77a", "#b8860b", "#fff1b8"];
export const goldConfetti = () => {
  const end = Date.now() + 2200;
  const rain = () => {
    confetti({ particleCount: 8, angle: 270, startVelocity: 12, spread: 90, gravity: 0.8, drift: 0.2, ticks: 320, scalar: 1.1, colors: GOLD, origin: { x: Math.random(), y: -0.05 }, zIndex: 9999 });
    if (Date.now() < end) requestAnimationFrame(rain);
  };
  rain();
};
const StoreCtx = createContext(null);
export const useStore = () => useContext(StoreCtx);

const loadCart = () => {
  try { return JSON.parse(localStorage.getItem("rk_cart") || "[]"); } catch { return []; }
};

export const StoreProvider = ({ children }) => {
  const [launch, setLaunch] = useState({ store_open: false, launch_at: null, spots_left: null, promo_active: false });
  const [now, setNow] = useState(Date.now());
  const [cart, setCart] = useState(loadCart);
  const [open, setOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [stock, setStock] = useState({});
  const [ref, setRef] = useState(() => localStorage.getItem("rk_ref") || "");
  const [vip, setVip] = useState("");
  const [photos, setPhotos] = useState({});

  useEffect(() => {
    axios.get(`${API}/launch`).then((r) => setLaunch(r.data)).catch(() => {});
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    axios.get(`${API}/stock-status`).then((r) => setStock(r.data)).catch(() => {});
    axios.get(`${API}/product-photos`).then((r) => setPhotos(r.data)).catch(() => {});
    const vipKey = new URLSearchParams(window.location.search).get("vip") || localStorage.getItem("rk_vip");
    if (vipKey) {
      axios.get(`${API}/vip/${vipKey}`).then(() => { localStorage.setItem("rk_vip", vipKey); setVip(vipKey); })
        .catch(() => localStorage.removeItem("rk_vip"));
    }
    const fromUrl = new URLSearchParams(window.location.search).get("ref");
    if (fromUrl) { localStorage.setItem("rk_ref", fromUrl.toUpperCase()); setRef(fromUrl.toUpperCase()); }
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { localStorage.setItem("rk_cart", JSON.stringify(cart)); }, [cart]);

  const launchAt = launch.launch_at ? new Date(launch.launch_at).getTime() : null;
  const left = launchAt ? Math.max(0, launchAt - now) : null;
  const storeOpen = launch.store_open || left === 0 || Boolean(vip);

  const add = useCallback((p) => {
    setCart((c) => {
      const hit = c.find((i) => i.sku === p.sku);
      return hit ? c.map((i) => (i.sku === p.sku ? { ...i, qty: Math.min(10, i.qty + 1) } : i))
        : [...c, { sku: p.sku, name: p.name, price: p.price, img: p.img, qty: 1 }];
    });
  }, []);
  const setQty = (sku, qty) => setCart((c) => (qty < 1 ? c.filter((i) => i.sku !== sku) : c.map((i) => (i.sku === sku ? { ...i, qty: Math.min(10, qty) } : i))));
  const count = cart.reduce((n, i) => n + i.qty, 0);

  return (
    <StoreCtx.Provider value={{ launch, left, storeOpen, spots: launch.spots_left, cart, add, setQty, setCart, count, open, setOpen, reviews, stock, ref, setRef, vip, photos }}>
      {children}
    </StoreCtx.Provider>
  );
};

export const CartButton = () => {
  const { storeOpen, count, setOpen } = useStore();
  if (!storeOpen && !count) return null;
  return (
    <button data-testid="header-cart-button" onClick={() => setOpen(true)} aria-label="Open cart" className="relative p-2 text-[#f5f5f0] hover:text-[#c5a059] transition-colors">
      <ShoppingBag size={22} />
      {count > 0 && (
        <span data-testid="header-cart-count" className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#c5a059] px-1 text-[10px] font-bold text-black">{count}</span>
      )}
    </button>
  );
};

const CartLine = ({ i, setQty }) => (
  <div data-testid={`cart-item-${i.sku}`} className="flex gap-3 py-4 border-b border-white/10">
    <img src={i.img} alt={i.name} className="h-16 w-16 rounded-sm object-cover border border-white/10" />
    <div className="flex-1 min-w-0">
      <p className="font-serif text-sm text-[#f5f5f0] leading-snug">{i.name}</p>
      <p className="mt-0.5 text-sm text-[#c5a059]">{money(i.price)}</p>
      <div className="mt-2 inline-flex items-center rounded-sm border border-white/15">
        <button data-testid={`cart-dec-${i.sku}`} onClick={() => setQty(i.sku, i.qty - 1)} className="px-2 py-1 hover:text-[#c5a059]"><Minus size={13} /></button>
        <span data-testid={`cart-qty-${i.sku}`} className="px-2 text-sm tabular-nums">{i.qty}</span>
        <button data-testid={`cart-inc-${i.sku}`} onClick={() => setQty(i.sku, i.qty + 1)} className="px-2 py-1 hover:text-[#c5a059]"><Plus size={13} /></button>
      </div>
    </div>
  </div>
);

const Row = ({ label, value, id, gold }) => (
  <div className="flex justify-between text-sm">
    <span className="text-[#f5f5f0]/75">{label}</span>
    <span data-testid={id} className={gold ? "text-[#d4af37] font-medium" : "text-[#f5f5f0]"}>{value}</span>
  </div>
);

const useQuote = (cart, code, setCode, ref, vip) => {
  const [q, setQ] = useState(null);
  useEffect(() => {
    if (!cart.length) { setQ(null); return; }
    const items = cart.map(({ sku, qty }) => ({ sku, qty }));
    axios.post(`${API}/payments/quote`, { items, origin_url: window.location.origin, code: code || null, ref: ref || null, vip: vip || null })
      .then((r) => setQ(r.data))
      .catch((e) => {
        if (code) { toast.error(e.response?.data?.detail || "That code didn't work."); setCode(""); } else setQ(null);
      });
  }, [cart, code, setCode, ref, vip]);
  return q;
};

const CodeBox = ({ code, setCode }) => {
  const [val, setVal] = useState("");
  if (code) {
    return (
      <div data-testid="cart-code-applied" className="flex items-center justify-between rounded-sm border border-[#c5a059]/40 px-3 py-2 text-xs text-[#d4af37]">
        <span>Code {code} applied</span>
        <button data-testid="cart-code-remove" onClick={() => setCode("")} className="underline">Remove</button>
      </div>
    );
  }
  return (
    <div className="flex gap-2">
      <input data-testid="cart-code-input" value={val} onChange={(e) => setVal(e.target.value.toUpperCase())} placeholder="Code or gift card" className="flex-1 rounded-sm bg-black border border-white/15 px-3 py-2 text-xs text-[#f5f5f0] focus:border-[#c5a059] outline-none" />
      <button data-testid="cart-code-apply" disabled={val.trim().length < 6} onClick={() => setCode(val.trim())} className="rounded-sm border border-[#c5a059] px-3 py-2 text-xs text-[#c5a059] hover:bg-[#c5a059] hover:text-black disabled:opacity-50 transition-colors">Apply</button>
    </div>
  );
};

export const CartDrawer = () => {
  const { cart, setQty, open, setOpen, storeOpen, launch, ref, vip } = useStore();
  const preorder = !storeOpen && cart.length > 0;
  const [code, setCode] = useState("");
  const q = useQuote(cart, code, setCode, ref, vip);
  const [busy, setBusy] = useState(false);
  const hasBundle = cart.some((i) => i.sku.startsWith("bundle_"));
  const hasSingle = cart.some((i) => !i.sku.startsWith("bundle_"));

  const checkout = async () => {
    setBusy(true);
    try {
      const items = cart.map(({ sku, qty }) => ({ sku, qty }));
      const r = await axios.post(`${API}/payments/checkout`, { items, origin_url: window.location.origin, code: code || null, ref: ref || null, vip: vip || null });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout couldn't start. Please try again.");
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0d0d0d] border-l border-[#c5a059]/30 flex flex-col">
        <div className="flex items-center justify-between px-6 h-20 border-b border-white/10">
          <h2 className="font-serif text-xl text-[#f5f5f0] flex items-center gap-2"><Crown size={18} className="text-[#c5a059]" /> Your Royal Order</h2>
          <button data-testid="cart-close" onClick={() => setOpen(false)} className="p-2 hover:text-[#c5a059]"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6">
          {!cart.length && <p data-testid="cart-empty" className="py-16 text-center text-[#f5f5f0]/60">Your cart is empty.</p>}
          {cart.map((i) => <CartLine key={i.sku} i={i} setQty={setQty} />)}
          {ref && q?.referral_applied && (
            <p data-testid="cart-ref-note" className="mt-4 text-xs text-[#f5f5f0]/60">A friend referred you — 15% off is applied to this order.</p>
          )}
          {launch.promo_active && cart.length > 0 && (
            <p data-testid="cart-promo-hint" className="mt-4 flex items-start gap-2 rounded-sm border border-[#c5a059]/40 bg-[#c5a059]/10 p-3 text-xs text-[#d4af37]">
              <Sparkles size={14} className="shrink-0 mt-0.5" />
              {hasBundle && hasSingle ? "Launch Day: 15% off every add-on in this order — applied automatically."
                : hasBundle ? "Don't leave the throne half-built. Add on and save 15%."
                : "Add a bundle and every single product gets 15% off — launch day only."}
            </p>
          )}
        </div>
        {cart.length > 0 && (
          <div className="border-t border-white/10 px-6 py-5 space-y-2">
            <CodeBox code={code} setCode={setCode} />
            {q && <>
              <Row label="Subtotal" value={money(q.subtotal)} id="cart-subtotal" />
              {q.discount > 0 && <Row label="Launch Day 15% off add-ons" value={`−${money(q.discount)}`} id="cart-discount" gold />}
              {q.thanks_discount > 0 && <Row label={`Royal code ${q.code_percent}% off`} value={`−${money(q.thanks_discount)}`} id="cart-thanks-discount" gold />}
              {q.gift_applied > 0 && <Row label="Gift card" value={`−${money(q.gift_applied)}`} id="cart-gift" gold />}
              {q.referral_discount > 0 && <Row label="Friend referral 15% off" value={`−${money(q.referral_discount)}`} id="cart-referral-discount" gold />}
              <Row label={<span className="inline-flex items-center gap-1"><Truck size={13} /> Shipping</span>} value={q.shipping ? money(q.shipping) : "FREE"} id="cart-shipping" gold={!q.shipping} />
              <div className="flex justify-between pt-2 border-t border-white/10 font-serif text-lg">
                <span>Total</span><span data-testid="cart-total" className="text-[#c5a059]">{money(q.total)}</span>
              </div>
              <p className="text-[11px] text-[#f5f5f0]/50">Free shipping over $100 and on the Majestic Mane. Tax calculated at checkout.</p>
            </>}
            {preorder && <p data-testid="cart-preorder-note" className="mt-3 rounded-sm border border-[#c5a059]/40 bg-[#c5a059]/10 px-3 py-2 text-xs text-[#d4af37]">Pre-order: ships in 7–10 days. Your card is charged today.</p>}
            <button data-testid="cart-checkout-button" disabled={busy || !(storeOpen || preorder)} onClick={checkout} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[#c5a059] py-4 text-sm font-semibold text-black hover:bg-[#d4af37] disabled:opacity-60 transition-colors">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Crown size={16} />} {storeOpen ? "Secure Checkout" : "Pre-order Now"}
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};

const usePaymentStatus = (sessionId, setInfo) => {
  const [status, setStatus] = useState(sessionId ? "checking" : "missing");
  useEffect(() => {
    if (!sessionId) return;
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          if (!r.data.referral_link && tries++ < 5) setTimeout(poll, 1500);
          setInfo(r.data);
          return;
        }
        if (["expired", "failed"].includes(r.data.payment_status)) { setStatus("failed"); return; }
      } catch { /* retry */ }
      if (!stop && ++tries < 15) setTimeout(poll, 2000); else if (!stop) setStatus("pending");
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);
  return status;
};

const ReferBox = ({ link, gift }) => {
  const copy = () => { navigator.clipboard.writeText(link).then(() => toast.success("Link copied")).catch(() => {}); };
  return (
    <div data-testid="refer-box" className="mt-8 rounded-sm border border-[#c5a059]/30 bg-[#121212] p-5 text-left">
      {gift && <p className="mb-4 text-sm text-[#f5f5f0]/80">Your thank-you gift: <span data-testid="refer-gift-code" className="font-semibold tracking-wider text-[#d4af37]">{gift}</span> — 10% off your next order.</p>}
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#c5a059]">Refer a King · Give 15%, Get 15%</div>
      <p className="mt-2 text-sm text-[#f5f5f0]/75">Your friend gets 15% off. When they buy, you get 15% off your next order.</p>
      <div className="mt-3 flex gap-2">
        <input data-testid="refer-link" readOnly value={link} className="flex-1 min-w-0 rounded-sm bg-black border border-white/15 px-3 py-2 text-xs text-[#f5f5f0]" />
        <button data-testid="refer-copy" onClick={copy} className="rounded-sm bg-[#c5a059] px-4 py-2 text-xs font-semibold text-black hover:bg-[#d4af37] transition-colors">Copy</button>
      </div>
    </div>
  );
};

export const PaymentResult = ({ cancelled }) => {
  const sessionId = new URLSearchParams(window.location.search).get("session_id");
  const [info, setInfo] = useState({});
  const status = usePaymentStatus(cancelled ? null : sessionId, setInfo);
  const isGift = new URLSearchParams(window.location.search).get("gift") === "1";
  const [hadBundle] = useState(() => loadCart().some((i) => BUNDLE_SKUS.includes(i.sku)));
  useEffect(() => { if (status === "paid" && hadBundle && !isGift) goldConfetti(); }, [status, hadBundle, isGift]);
  useEffect(() => { if (status === "paid" && !isGift) localStorage.removeItem("rk_cart"); }, [status, isGift]);
  const msg = cancelled ? ["Checkout cancelled", "No charge was made. Your cart is still waiting for you."]
    : { checking: ["Confirming your order…", "One moment, King."],
        paid: isGift ? ["A gift fit for a king.", "Your gift card is on its way to their inbox. A receipt is on its way to yours."]
          : ["Long live the King.", "Your order is confirmed. A receipt is on its way to your email."],
        pending: ["Payment processing", "We're still confirming your payment — check your email shortly."],
        failed: ["Payment didn't go through", "No charge was made. Please try again."],
        missing: ["Order not found", "Head back to the store to continue."] }[status];
  return (
    <div data-testid="payment-result" className="min-h-screen bg-[#0a0a0a] text-[#f5f5f0] flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        {status === "checking" ? <Loader2 className="mx-auto animate-spin text-[#c5a059]" size={40} /> : status === "paid" ? <CheckCircle2 className="mx-auto text-[#c5a059]" size={48} /> : <Crown className="mx-auto text-[#c5a059]" size={44} />}
        <h1 data-testid="payment-result-title" className="mt-6 font-serif text-4xl sm:text-5xl">{msg[0]}</h1>
        <p className="mt-4 text-[#f5f5f0]/75">{msg[1]}</p>
        {status === "paid" && <p className="mt-4 italic text-[#c5a059]">Prepare to step in as a man and walk out as a king.</p>}
        {status === "paid" && info.referral_link && <ReferBox link={info.referral_link} gift={info.thank_you_code} />}
        <a data-testid="payment-back-home" href="/" className="mt-8 inline-flex items-center gap-2 rounded-sm bg-[#c5a059] px-8 py-3 text-sm font-medium text-black hover:bg-[#d4af37] transition-colors">
          <Crown size={16} /> Back to the Kingdom
        </a>
      </div>
    </div>
  );
};
